import React, { useState, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import Button from "@mui/material/Button";
import { useQuery } from "@apollo/client/react";

import { INTERNAL_WORK_ORDERS_QUERY } from "../../constants/graphql/queries";
import { fullWidthDialog } from "../../constants/dialog";

const rowsPerPage = 20;

function formatInternalWorkOrderNumber(order) {
  if (order.revisionNumber > 0) {
    return `${order.internalWorkOrderNumber}.R${order.revisionNumber}`;
  }

  return order.internalWorkOrderNumber || order.uid || order.id;
}

export default function InternalWorkOrderPickerModal({
  open = true,
  onClose,
  onSelect,
  projectId,
}) {
  const dialogContentRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [page, setPage] = useState(0);

  const { loading, data, refetch } = useQuery(INTERNAL_WORK_ORDERS_QUERY, {
    variables: {
      limit: rowsPerPage,
      page: page + 1,
      search: searchTerm,
      projectId: projectId ? parseInt(projectId, 10) : null,
    },
  });

  const workOrders = data?.internalWorkOrders?.collection || [];
  const totalCount = data?.internalWorkOrders?.metadata?.totalCount || 0;

  const handleSearch = () => {
    setPage(0);
    setSearchTerm(searchValue);
    refetch();
  };

  const handleKeyPress = e => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleSelect = workOrder => {
    setSelectedWorkOrder(workOrder);
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage - 1);
    if (dialogContentRef.current) {
      dialogContentRef.current.scrollTop = 0;
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      onClose={onClose}
      sx={{
        ...fullWidthDialog,
        "& .MuiDialog-paper": {
          ...fullWidthDialog["& .MuiDialog-paper"],
          maxHeight: { xs: "78vh", sm: "50vh" },
          width: { xs: "100%", sm: "900px" },
          maxWidth: { xs: "100%", sm: "900px" },
        },
      }}
      PaperProps={{
        sx: {
          maxHeight: { xs: "78vh", sm: "50vh" },
          width: { xs: "100%", sm: "900px" },
          maxWidth: { xs: "100%", sm: "900px" },
          margin: { xs: 0, sm: 4 },
        },
      }}
    >
      <DialogTitle sx={{ position: "relative" }}>
        <Typography fontSize={22} fontWeight={500} color="textPrimary" align="center">
          Select Internal Work Order
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <CloseIcon color="action" />
        </IconButton>
      </DialogTitle>
      <DialogContent ref={dialogContentRef}>
        <Box mt={1} mb={3} sx={{ position: "sticky", top: 0, backgroundColor: "white", zIndex: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Search"
            variant="outlined"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <DialogContentText>
          {loading && <LinearProgress />}
          {!loading && workOrders.length === 0 && (
            <Typography variant="body1" align="center" sx={{ my: 4 }}>
              No internal work orders found matching your search.
            </Typography>
          )}

          {workOrders.length > 0 && (
            <Paper>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        Internal Order #
                      </TableCell>
                      <TableCell align="center">Requested By</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workOrders.map(order => (
                      <TableRow
                        key={order.id}
                        selected={selectedWorkOrder?.id === order.id}
                        hover
                        onClick={() => handleSelect(order)}
                      >
                        <TableCell align="center">
                          {formatInternalWorkOrderNumber(order)}
                        </TableCell>
                        <TableCell align="center">{order?.user?.email}</TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => onSelect(order)}
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            Select Order
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", p: 2 }}>
        {totalCount > rowsPerPage && (
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage)}
            page={page + 1}
            onChange={handleChangePage}
            color="primary"
            showFirstButton
            showLastButton
          />
        )}
      </DialogActions>
    </Dialog>
  );
}
